import { useEffect, useRef, useMemo } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  ButtonSecondary,
  ButtonSize,
  Filters,
  useFilters,
  PermissionEnum,
  ButtonPrimary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  getCards,
  onSortList,
  onFilterList,
  selectList,
  changeStatus,
} from './store/slices/list';
import {
  cardsListColumns,
  cardsListSchema,
  cardsListFiltersSchema,
  cardsListColumnsLabels,
} from './variables/list';
import { Card, CardStatus } from './types';
import formatBalance from '../helpers/format-balance';
import { useGetCustomersQuery } from './store/services';

import { CardsRoutes } from '../../routes';

import cn from './cards.module.css';

const formatCardsList = (list: Card[]): Card[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      cardType: <div>{`${item.paymentSystem} ${item.cardType}`}</div>,
      balance: <div>{item.balance ? formatBalance(item.balance) : '-'}</div>,
      virtual: <div>{item.virtual ? 'Virtual' : 'Physical'}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const CardsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    cards: {
      list: {
        isLoading,
        values,
        requestInProgress,
        selectedList,
        changeStatusInProgress,
      },
    },
    configurationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useGetCustomersQuery();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(cardsListSchema);

  const formattedData = useMemo(() => {
    const data = formatCardsList(values as Card[]);

    return data;
  }, [values, sort, filters]);

  const onSort = (name: string) => {
    onSortChange(name);

    const variables: { name: string; direction: 'asc' | 'desc' } = {
      name,
      direction: sort.sortDir === 'asc' ? 'desc' : 'asc',
    };

    dispatch(onSortList(variables));
  };

  const onFilter = (filterValues: Filters) => {
    onFilterChange(filterValues);

    dispatch(onFilterList(filterValues));
  };

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedList, rows)) {
      dispatch(selectList(rows));
    }
  };

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: cardsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({ element: scrollRef, requestInProgress });

  useEffect(() => {
    if (!values) {
      dispatch(
        getCards({
          name: sort?.sortProp,
          direction: sort?.sortDir as 'asc' | 'desc',
          filters,
        }),
      );
    }
  }, [dispatch, sort, values]);

  useEffect(() => {
    let title = 'Cards';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.cardsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={cardsListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={cardsListColumnsLabels}
        onFilterChange={(filterValues) => onFilter(filterValues)}
        filtersComponent={filtersComponent}
        isLoading={requestInProgress || changeStatusInProgress}
        handleSubmit={handleSubmit}
        onRowsSelect={onRowsSelect}
        clickable
      />
    );
  }

  return (
    <div className={cn.cardsBottom} ref={scrollRef}>
      <div className={cn.cardsTopWrapper}>
        <div className={cn.cardsTitle}>Cards</div>
        <ButtonPrimary
          size={ButtonSize.SMALL}
          text="Order a card"
          className={cn.orderCardButton}
          onClick={() =>
            navigate(`${CardsRoutes.CARDS_LIST_ORDER}${search || ''}`)
          }
        />
        {(activePermissions?.includes(PermissionEnum.CARDS_ACTIVATE_ACTION) ||
          activePermissions?.includes(PermissionEnum.CARDS_UNFREEZE_ACTION) ||
          activePermissions?.includes(PermissionEnum.CARDS_CANCEL_ACTION) ||
          activePermissions?.includes(PermissionEnum.CARDS_FREEZE_ACTION) ||
          activePermissions?.includes(PermissionEnum.CARDS_ACCEPT_ACTION)) && (
          <div className={cn.cardsStatuses}>
            {activePermissions?.includes(
              PermissionEnum.CARDS_ACTIVATE_ACTION,
            ) && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                disabled={
                  Boolean(!selectedList.length) ||
                  Boolean(changeStatusInProgress)
                }
                text="Activate"
                onClick={() => dispatch(changeStatus(CardStatus.ACTIVE))}
                className={`${cn.cardStatusButton} button-white`}
              />
            )}
            {activePermissions?.includes(
              PermissionEnum.CARDS_UNFREEZE_ACTION,
            ) && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                disabled={
                  Boolean(!selectedList.length) ||
                  Boolean(changeStatusInProgress)
                }
                text="Process"
                onClick={() => dispatch(changeStatus(CardStatus.IN_PROCESS))}
                className={`${cn.cardStatusButton} button-white`}
              />
            )}
            {activePermissions?.includes(
              PermissionEnum.CARDS_CANCEL_ACTION,
            ) && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                disabled={
                  Boolean(!selectedList.length) ||
                  Boolean(changeStatusInProgress)
                }
                text="Cancel"
                onClick={() => dispatch(changeStatus(CardStatus.CANCELED))}
                className={`${cn.cardStatusButton} button-white`}
              />
            )}
            {activePermissions?.includes(
              PermissionEnum.CARDS_FREEZE_ACTION,
            ) && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                disabled={
                  Boolean(!selectedList.length) ||
                  Boolean(changeStatusInProgress)
                }
                text="Freeze"
                onClick={() => dispatch(changeStatus(CardStatus.FROZEN))}
                className={`${cn.cardStatusButton} button-white`}
              />
            )}
            {activePermissions?.includes(
              PermissionEnum.CARDS_ACCEPT_ACTION,
            ) && (
              <ButtonSecondary
                size={ButtonSize.SMALL}
                disabled={
                  Boolean(!selectedList.length) ||
                  Boolean(changeStatusInProgress)
                }
                text="Approve"
                onClick={() => dispatch(changeStatus(CardStatus.APPROVED))}
                className="button-white"
              />
            )}
          </div>
        )}
      </div>
      {content}
      <Outlet />
    </div>
  );
};

export default CardsList;
